import { computed, DestroyRef, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { withGridConfigFeatures } from '@data-access/shared/store/features/with-grid-config-features.store';
import { GridType } from '@data-access/grid-views';
import { withDocumentTypeEntities } from '@data-access/documents/store/features/with-document-types.entities';
import { withMimeTypesEntities } from '@data-access/documents/store/features/with-mime-types.entities';
import {
  DocumentTypeExtended,
  EditDocument,
  UploadDocument,
  UploadDocumentPayload,
  UploadDocumentSignalState,
} from '@data-access/documents/types';
import {
  UPLOAD_DOCUMENT_GRID_CONFIG,
  uploadDocumentGridInitialState,
} from '@data-access/documents/contants';
import { withUploadDocumentEntities } from './entities';
import { UploadState } from '@data-access/submissions';
import { withGridCheckBoxFeature } from '@data-access/shared/store/features/with-grid-checkbox-feature.store';
import { DIALOG_TYPE, OpenDialogState } from '@data-access/shared';
import { DocumentsService } from '@data-access/documents/services/documents.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const uploadDocumentSignalStore = signalStore(
  withState(() => uploadDocumentGridInitialState),

  withProps(
    (
      store,
      _documentService = inject(DocumentsService),
      _destroyRef = inject(DestroyRef),
    ) => ({
      _injector: inject(Injector),
      store,
      _documentService,
      _destroyRef,
    }),
  ),
  withFeature(({ _injector }) => withDocumentTypeEntities(_injector)),
  withFeature(({ _injector }) => withMimeTypesEntities(_injector)),
  withGridCheckBoxFeature(),
  withGridConfigFeatures(),
  withUploadDocumentEntities(),
  withComputed(
    ({
      error,
      canCloseDialog,
      _uploadDocumentEntities,
      selectedView,
      total,
      isLoading,
      _documentTypesEntities,
      _mimeTypeEntities,
      store,
    }) => ({
      $error: computed(() => error()),
      $canDialogClose: computed(() => canCloseDialog()),
      $documentTypes: computed(() => {
        const _documentTypes = _documentTypesEntities();
        if (!_documentTypes) {
          return [] as DocumentTypeExtended[];
        }
        return _documentTypes;
      }),
      $mimeTypeEntities: computed(() => {
        const entities = _mimeTypeEntities();
        return entities || [];
      }),
      $documents: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return [] as UploadDocument[];
        }
        return _documents;
      }),
      $documentUploaded: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return false;
        }
        return _documents.every(
          (doc) => !['pending', 'uploading'].includes(doc.uploadState),
        );
      }),
      $uploadFailedDocuments: computed(() => {
        const _documents = _uploadDocumentEntities();
        if (!_documents) {
          return [] as UploadDocument[];
        }
        return _documents.filter((doc) => doc.uploadState === 'failed');
      }),
      $isLoading: computed(() => isLoading()),
      $selectedView: computed(() => selectedView()),
      $totalCount: computed(() => total()),
      $gridType: computed(() => 'submissionUploadDocuments' as GridType),
      $isAllSelected: computed(() => store.isAllSelected()),
      $isIndeterminate: computed(() => store.isIndeterminate()),
      $selectedRows: computed(() => store.selectedRows()),
    }),
  ),
  withMethods(({ _documentService, _destroyRef, ...store }) => {
    const _loadDocumentGrid = (documents: UploadDocument[]) => {
      store.loadUploadDocument(documents);
      store.loadConfigurations(UPLOAD_DOCUMENT_GRID_CONFIG);
      store.setGridViews([]);
      store.setAllRows(store.selectedRows(), documents);
      patchState(store, {
        total: documents.length,
      });
    };

    const _updateDocumentGrid = (document: UploadDocument) => {
      store.updateDocumentGrid(document);
      patchState(store, {
        isLoading: false,
        canCloseDialog: true,
      });
    };

    const _updateFileStatus = (
      fileName: string,
      uploadState: UploadState,
      uploadError: string | null = null,
    ) => {
      const _documents = store.$documents();
      const _updateDoc = _documents.find((doc) => doc.file.name === fileName);

      if (_updateDoc) {
        store.updateDocumentGrid({
          ..._updateDoc,
          uploadError,
          uploadState,
        });
      }
    };

    const _updateDocuments = (editDocument: EditDocument) => {
      const _documents = store.selectedRows();
      _documents.forEach((doc) => {
        doc.taskType.get('value')?.setValue(editDocument.tasks || []);
        doc.documentType
          .get('value')
          ?.setValue(editDocument.document_type || null);
        doc.document_type_name = editDocument.document_type?.label || '';
        _updateDocumentGrid(doc);
      });
      patchState(store, {
        selectedRows: [],
        isAllSelected: false,
        isIndeterminate: false,
      });
    };

    const _openEditDocumentDialog = (dialogMetaData: OpenDialogState) => {
      store.openDialog({
        component: () => dialogMetaData.component(),
        componentData: dialogMetaData.componentData,
        type: DIALOG_TYPE.GroupFields,
      });
    };

    const _openUploadDocumentStatusDialog = (
      dialogMetaData: OpenDialogState,
    ) => {
      store.openDialog({
        component: () => dialogMetaData.component(),
        componentData: dialogMetaData.componentData,
        type: DIALOG_TYPE.GroupFields,
        config: { disableClose: true },
      });
    };

    const _uploadDocuments = (documents: UploadDocumentPayload[]) => {
      documents.forEach((doc) => {
        _updateFileStatus(doc.file.name, 'uploading');
        _documentService
          .uploadDocuments(doc)
          .pipe(takeUntilDestroyed(_destroyRef))
          .subscribe({
            error: (err) => {
              _updateFileStatus(doc.file.name, 'failed', err);
            },
            next: () => _updateFileStatus(doc.file.name, 'completed'),
          });
      });
    };

    return {
      loadDocumentGrid: (documents: UploadDocument[]) =>
        _loadDocumentGrid(documents),
      updateDocumentGrid: (document: UploadDocument) =>
        _updateDocumentGrid(document),
      loadDocumentTypes: (params: { workspaceId?: string }) => {
        patchState(store, {
          isLoading: false,
          currentStateDestroyed: false,
        });
        store.setDocumentTypes(params);
      },
      loadMimeTypes: () => {
        store.setMimeTypes();
      },
      resetGridSettings: () => {},
      resetState: () => {
        patchState(store, {
          ...uploadDocumentGridInitialState,
          currentStateDestroyed: true,
          error: null,
          isLoading: false,
        });
        store.loadUploadDocument([]);
      },
      selectAll: (checked: boolean) =>
        store.selectAll(store.selectedRows(), store.allRows(), checked),

      selectRow: (row: UploadDocument, checked: boolean) =>
        store.selectRow(store.selectedRows(), store.allRows(), row, checked),

      updateRows: (rows: UploadDocument[]) => {
        patchState(store, {
          selectedRows: [],
          isAllSelected: false,
          isIndeterminate: false,
          allRows: rows,
        });
        _loadDocumentGrid(rows);
      },

      openEditDocumentDialog: (dialogMetaData: OpenDialogState) => {
        patchState(store, { canCloseDialog: false });
        _openEditDocumentDialog(dialogMetaData);
      },

      updateDocument: (document: EditDocument) => _updateDocuments(document),
      openUploadDocumentStatusDialog: (
        dialogMetaData: OpenDialogState,
        documents: UploadDocumentPayload[],
      ) => {
        patchState(store, { canCloseDialog: false });
        _openUploadDocumentStatusDialog(dialogMetaData);
        _uploadDocuments(documents);
      },
      resetSelectedRows: () =>
        patchState(store, {
          selectedRows: [],
          isAllSelected: false,
          isIndeterminate: false,
        }),
      updateFileStatus: (
        fileName: string,
        uploadState: UploadState,
        error?: string | null,
      ) => _updateFileStatus(fileName, uploadState, error),
    };
  }),
) satisfies Type<UploadDocumentSignalState>;
